import type { ApprovalMode, ProjectRunFlags, RpaSegmentedTask } from '../../contracts';
import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import { serializeLinkedContext } from '../../contracts/projects/linked-context';
import {
  buildProjectsComposerMessage,
  isProjectsComposerSubmittable,
  projectsComposerSubmitPrompts,
} from './build-projects-composer-message';
import {
  compileSegmentedTask,
  resolveAvailableWorkflowSkill,
} from './compile-segmented-task';
import { composerLinkedContext } from './composer-linked-context';
import {
  getProjectsComposerWorkflow,
  hasProjectsComposerSkill,
  projectsComposerDraftWireText,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerSkillReference,
} from './projects-composer-editor-draft';

export interface BuildComposerSubmissionInput {
  draft: ProjectsComposerPromptDraft;
  routinesOffered: boolean;
  offeredSkills: readonly ProjectsComposerSkillReference[];
  linkedItems: readonly LinkedContextItem[];
  runFlags: ProjectRunFlags;
  approvalMode: ApprovalMode | null;
  disabled: boolean;
  sendDisabled: boolean;
  isSubmitting: boolean;
}

export type ComposerSubmission =
  | { kind: 'blocked' }
  | {
      kind: 'workflow';
      workflowId: string;
      /** What the clinician added next to the routine chip, as wire text. Empty when nothing. */
      prompt: string;
      /** The picked session chip, sent as the run's target. */
      sessionId?: string;
      /** The picked patient chip's profile id, sent as the run's target. */
      patientProfileId?: string;
    }
  | { kind: 'skill_unavailable'; error: unknown }
  | {
      kind: 'submit';
      text: string;
      displayText: string;
      computerUseSkill?: RpaSegmentedTask;
      runFlags: ProjectRunFlags;
      approvalMode?: ApprovalMode;
    };

export const buildComposerSubmission = ({
  draft,
  routinesOffered,
  offeredSkills,
  linkedItems,
  runFlags,
  approvalMode,
  disabled,
  sendDisabled,
  isSubmitting,
}: BuildComposerSubmissionInput): ComposerSubmission => {
  const draftHasSkill = hasProjectsComposerSkill(draft);
  if (
    !isProjectsComposerSubmittable(draft.text, routinesOffered && draftHasSkill) ||
    disabled ||
    sendDisabled ||
    isSubmitting
  ) {
    return { kind: 'blocked' };
  }

  const workflowRef = routinesOffered ? getProjectsComposerWorkflow(draft) : undefined;
  if (workflowRef) {
    // Single-slot like a project create: only the first picked chip becomes the
    // run's target. Inline `@` tokens stay reference tags in the text.
    const linked = linkedItems[0];
    return {
      kind: 'workflow',
      workflowId: workflowRef.skillId,
      prompt: buildProjectsComposerMessage({
        linkedContextPreamble: serializeLinkedContext(
          composerLinkedContext(draft, linkedItems)
        ),
        text: projectsComposerDraftWireText(draft.segments),
      }),
      ...(linked?.type === 'session' ? { sessionId: linked.sessionId } : {}),
      ...(linked?.type === 'patient' && linked.patientId
        ? { patientProfileId: linked.patientId }
        : {}),
    };
  }

  let computerUseSkill: RpaSegmentedTask | undefined;
  if (routinesOffered && draftHasSkill) {
    try {
      computerUseSkill = compileSegmentedTask(
        draft,
        resolveAvailableWorkflowSkill(offeredSkills)
      );
    } catch (error) {
      // The consumer localizes via formatCompileSegmentedTaskError; the raw
      // error keeps its English developer-facing message.
      return { kind: 'skill_unavailable', error };
    }
  }

  // XXX: pre-existing, owned by Evidence: the Evidence handoff sends this wire
  // text as `?query=`, so a linked chip's preamble (names, ids) rides in the URL.
  const { wireText, displayText } = projectsComposerSubmitPrompts({
    draft,
    linkedContextPreamble: serializeLinkedContext(
      composerLinkedContext(draft, linkedItems)
    ),
    computerUseSkill,
  });

  return {
    kind: 'submit',
    text: wireText,
    displayText,
    computerUseSkill,
    runFlags,
    approvalMode: approvalMode ?? undefined,
  };
};
